import { customizationFlagsQueryKeys } from 'components/customization-page-container/customization-page.queries';
import { useQueryClient } from 'react-query';
import {
  ChecklistMetricCriteria,
  ChecklistMetricStatus,
  MultiLocationResult,
} from '../../handoff-checklist-v2/handoff-checklist-v2.types';
import {
  UnifyMetricEvalCommonData,
  UnifyMetricEvalContext,
  UnifyMetricResult,
} from '../unify-metrics.types';
import { useSelector } from 'react-redux';
import { selectCurrentLocationV2 } from 'redux/actions/location/locations/locationsV2.selectors';
import { selectCurrentLocationParentId } from 'redux/actions/location/current-location';
import { selectPaymentUrl } from 'redux/actions/merchant/merchant.action';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UNIFY_METRIC_DEFINITIONS } from '../unify-metric-definitions';
import { fetchLocationContextData } from '../utils/unify-handoff.util';
import { runWithConcurrency } from '../utils/run-with-concurrency';
import { getParsedErrorMessage } from 'redux/axios';
import { deriveOverallStatus } from '../../handoff-checklist-v2/handoff-checklist-v2.helpers';
import { UnifyLocationRow } from '../components/unify-locations-table/unify-locations-table.types';
import { UnifyHandoffQueryKeys } from '../queries/unify-handoff.queries';

interface UseUnifyHandoffMetricsReturnValues {
  data: UnifyMetricResult[];
  error: string | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

interface UseUnifyHandoffMetricsProps {
  locations?: UnifyLocationRow[];
  enabled?: boolean;
}

export const useUnifyHandoffMetricsEvaluation = ({
  locations,
  enabled = true,
}: UseUnifyHandoffMetricsProps): UseUnifyHandoffMetricsReturnValues => {
  const commonData = useUnifyMetricEvalCommonData();
  const [metricsResults, setMetricsResults] = useState<UnifyMetricResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const generationRef = useRef<number>(0);

  const refetch = useCallback(async () => {
    if (!locations?.length) {
      return;
    }

    setError(null);
    setIsLoading(true);
    const generationCounter = ++generationRef.current;

    // Invalidate cached evaluation and context queries for the locations being evaluated
    try {
      await Promise.all(
        locations.flatMap((loc) => [
          commonData.queryClient.invalidateQueries(
            UnifyHandoffQueryKeys.location(loc.locationId)
          ),
          commonData.queryClient.invalidateQueries(
            customizationFlagsQueryKeys.locationFeatures(loc.locationId)
          ),
        ])
      );
    } catch (e) {
      console.warn('Failed to invalidate queries on refetch:', e);
    }

    if (generationCounter !== generationRef.current) {
      return;
    }

    let locationEvalContextResults: UnifyMetricEvalContext[] = [];

    // Step 1: Fetch context data for all locations
    try {
      locationEvalContextResults = await Promise.all(
        locations.map((loc) =>
          fetchLocationContextData({
            locationId: loc.locationId,
            locationName: loc.locationName,
            verticalId: loc.verticalId,
            ...commonData,
          })
        )
      );
    } catch (error) {
      setError(getParsedErrorMessage(error));
      console.error('Error evaluating metrics context:', error);
      if (generationCounter === generationRef.current) {
        setIsLoading(false);
      }
      return;
    }

    // Bail early if a newer refetch call has already started
    if (generationCounter !== generationRef.current) {
      return;
    }

    type CriteriaTask = {
      locationContext: UnifyMetricEvalContext;
      metricDefinition: typeof UNIFY_METRIC_DEFINITIONS[number];
      criteriaDefinition: typeof UNIFY_METRIC_DEFINITIONS[number]['criteria'][number];
    };

    // Step 2: Build one metric row per metric (grouped across all its locations), a flat
    // list of criteria tasks to evaluate, and how many criteria each metric is still waiting on
    const allCriteriaToEvaluate: CriteriaTask[] = [];
    const initialMetrics: UnifyMetricResult[] = [];
    const locationResultsByMetricId = new Map<string, MultiLocationResult[]>();
    const remainingCriteriaCountByMetricId = new Map<string, number>();

    for (const metricDefinition of UNIFY_METRIC_DEFINITIONS) {
      const locationResults: MultiLocationResult[] = [];
      let remainingCriteriaCount = 0;

      for (const locationContext of locationEvalContextResults) {
        if (metricDefinition.shouldSkip?.(locationContext)) {
          continue;
        }

        const criterias: ChecklistMetricCriteria[] = [];
        for (const criteriaDefinition of metricDefinition.criteria) {
          if (criteriaDefinition.shouldSkip?.(locationContext)) {
            continue;
          }
          criterias.push({
            description: criteriaDefinition.description,
            status: ChecklistMetricStatus.PENDING,
            exceptionOptions: criteriaDefinition.exceptionOptions,
            exceptionType: criteriaDefinition.exceptionType,
          });
          allCriteriaToEvaluate.push({
            locationContext,
            metricDefinition,
            criteriaDefinition,
          });
          remainingCriteriaCount++;
        }

        locationResults.push({
          locationId: locationContext.locationId,
          locationName: locationContext.locationName,
          criterias,
        });
      }

      // Metric is skipped for every location, so it shouldn't be shown at all
      if (locationResults.length === 0) {
        continue;
      }

      initialMetrics.push({
        ...metricDefinition,
        isRequired: metricDefinition.isRequired ?? true,
        isLoading: true,
        status: ChecklistMetricStatus.PENDING,
        locationResults,
      });
      locationResultsByMetricId.set(metricDefinition.id, locationResults);
      remainingCriteriaCountByMetricId.set(metricDefinition.id, remainingCriteriaCount);
    }

    setMetricsResults(initialMetrics);

    // A metric's state is only flushed once every location's every criteria has settled,
    // so the UI never shows one location resolved while another is still pending for it.
    const flushMetricIfDone = (
      metricDefinition: typeof UNIFY_METRIC_DEFINITIONS[number]
    ) => {
      if (generationCounter !== generationRef.current) {
        return;
      }

      const remaining = remainingCriteriaCountByMetricId.get(metricDefinition.id) ?? 0;
      if (remaining > 0) {
        return;
      }

      const locationResults = locationResultsByMetricId.get(metricDefinition.id) ?? [];
      const overallStatus = deriveOverallStatus(
        locationResults.flatMap((lr) => lr.criterias.map((c) => c.status))
      );

      setMetricsResults((prev) =>
        prev.map((metric) =>
          metric.id === metricDefinition.id
            ? { ...metric, isLoading: false, status: overallStatus, locationResults }
            : metric
        )
      );
    };

    // Metrics with no criteria to evaluate (e.g. skipped everywhere already handled above,
    // or all criteria skipped for every location) can resolve immediately
    for (const metricDefinition of UNIFY_METRIC_DEFINITIONS) {
      if (remainingCriteriaCountByMetricId.has(metricDefinition.id)) {
        flushMetricIfDone(metricDefinition);
      }
    }

    // Step 3: Evaluate every criteria task with a limit on concurrency, flushing each
    // metric's state as soon as all of its locations' criteria have settled
    await runWithConcurrency(
      allCriteriaToEvaluate,
      async ({ locationContext, metricDefinition, criteriaDefinition }) => {
        if (generationCounter !== generationRef.current) {
          return;
        }

        let result: ChecklistMetricCriteria;
        try {
          const evaluated = await criteriaDefinition.evaluate(locationContext);
          result = { description: criteriaDefinition.description, ...evaluated };
        } catch (error) {
          console.error('Error evaluating criteria:', error);
          result = {
            description: criteriaDefinition.description,
            status: ChecklistMetricStatus.BLOCKED,
            message: getParsedErrorMessage(error) || 'Failed to evaluate criteria.',
          };
        }

        if (generationCounter !== generationRef.current) {
          return;
        }

        const locationResults = locationResultsByMetricId.get(metricDefinition.id) ?? [];
        const locationResult = locationResults.find(
          (lr) => lr.locationId === locationContext.locationId
        );
        const criteria = locationResult?.criterias.find(
          (c) => c.description === criteriaDefinition.description
        );
        if (criteria) {
          Object.assign(criteria, result);
        }

        remainingCriteriaCountByMetricId.set(
          metricDefinition.id,
          (remainingCriteriaCountByMetricId.get(metricDefinition.id) ?? 1) - 1
        );

        flushMetricIfDone(metricDefinition);
      },
      5
    );
    if (generationCounter === generationRef.current) {
      setIsLoading(false);
    }
  }, [commonData, locations]);

  const locationIds = locations?.map((location) => location.locationId).join(',') ?? '';

  useEffect(() => {
    if (enabled && locationIds) {
      refetch();
    }
  }, [enabled, locationIds, refetch]);

  return {
    data: metricsResults,
    error,
    isLoading,
    refetch,
  };
};

// These values are common for all children locations in unify handoff
const useUnifyMetricEvalCommonData = (): UnifyMetricEvalCommonData => {
  const queryClient = useQueryClient();
  const locationV2 = useSelector(selectCurrentLocationV2);
  const orgId = locationV2?.orgId;
  const parentLocationId = useSelector(selectCurrentLocationParentId);
  const paymentsUrl = useSelector(selectPaymentUrl);

  return useMemo(
    () => ({
      queryClient,
      orgId,
      parentLocationId,
      paymentsUrl,
    }),
    [queryClient, orgId, parentLocationId, paymentsUrl]
  );
};
